import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  Typography,
  List,
  ListItemText,
  ListItemButton,
  Divider,
  ListItem
} from '@mui/material';
import Grid from '@mui/material/Grid';

import { useNavigate } from 'react-router-dom';
import {
  appointmentsService,
  Appointment,
  Doctor
} from '@services/appointmentsService';
import { visitsService, CreateVisitData } from '@services/visitsService';
import { useAuth } from '@contexts/AuthContext';
import { formatForDisplay } from '@utils/dateFormatter';

interface StartVisitDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  petId: string;
  ownerId: string;
  petName: string;
}

const StartVisitDialog: React.FC<StartVisitDialogProps> = ({
  open,
  onClose,
  onSuccess,
  petId,
  ownerId,
  petName
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existingAppointments, setExistingAppointments] = useState<Appointment[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [openVisit, setOpenVisit] = useState<any>(null);
  
  // Form data for ad-hoc visit
  const [formData, setFormData] = useState({
    doctorId: '',
    reason: '',
    notes: ''
  });

  const loadData = useCallback(async () => {
    if (!user?.clinicId) return;

    try {
      setLoading(true);
      
      // Check for existing appointments today
      const now = new Date();
      const twoHoursAgo = new Date(now.getTime() - 2 * 60 * 60 * 1000);
      const twoHoursFromNow = new Date(now.getTime() + 2 * 60 * 60 * 1000);

      const [appointmentsData, doctorsData, openVisitData] = await Promise.all([
        appointmentsService.getAppointments({
          clinicId: user.clinicId,
          patientId: petId,
          from: twoHoursAgo.toISOString(),
          to: twoHoursFromNow.toISOString(),
          status: 'confirmed,in_progress'
        }),
        appointmentsService.getDoctors(user.clinicId),
        visitsService.checkOpenVisit(petId)
      ]);

      setExistingAppointments(appointmentsData.appointments || []);
      setDoctors(doctorsData);
      setOpenVisit(openVisitData);

      // Set default doctor to current user if they are a doctor
      if (doctorsData.length > 0) {
        const currentUserDoctor = doctorsData.find(d => d.id === user.id);
        if (currentUserDoctor) {
          setFormData(prev => ({ ...prev, doctorId: currentUserDoctor.id }));
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  }, [user?.clinicId, user?.id, petId]);

  useEffect(() => {
    if (open && user?.clinicId) {
      void loadData();
    }
  }, [open, user?.clinicId, loadData]);

  const handleStartFromAppointment = async (appointment: Appointment) => {
    try {
      setLoading(true);
      setError(null);

      const result = await appointmentsService.convertToVisit(appointment.id);
      
      onSuccess();
      onClose();
      navigate(`/visits/${result.visitId}`);
    } catch (error: any) {
      console.error('Error starting visit from appointment:', error);
      setError(error.response?.data?.error || 'Eroare la inceperea vizitei');
    } finally {
      setLoading(false);
    }
  };

  const handleStartAdHocVisit = async () => {
    if (!user?.clinicId) return;

    try {
      setLoading(true);
      setError(null);

      const visitData: CreateVisitData = {
        clinicId: user.clinicId,
        petId,
        ownerId,
        doctorId: formData.doctorId,
        reason: formData.reason,
        notes: formData.notes
      };

      const visit = await visitsService.createVisit(visitData);
      
      onSuccess();
      onClose();
      navigate(`/visits/${visit.id}`);
    } catch (error: any) {
      console.error('Error starting ad-hoc visit:', error);
      setError(error.response?.data?.error || 'Eroare la inceperea vizitei');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedAppointment(null);
    setFormData({
      doctorId: '',
      reason: '',
      notes: ''
    });
    setError(null);
    onClose();
  };

  if (openVisit) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Vizita Deschisa Exista</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Exista deja o vizita deschisa pentru {petName}.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Vrei sa continui cu vizita existenta?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Anuleaza</Button>
          <Button 
            variant="contained" 
            onClick={() => {
              onClose();
              navigate(`/visits/${openVisit.id}`);
            }}
          >
            Continua Vizita Exista
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Incepe Vizita pentru {petName}
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {existingAppointments.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Programari Existente (urmatoarele 2 ore)
            </Typography>
            <List>
              {existingAppointments.map((appointment, index) => (
                <React.Fragment key={appointment.id}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => setSelectedAppointment(appointment)}
                      selected={selectedAppointment?.id === appointment.id}
                    >
                      <ListItemText
                        primary={`${formatForDisplay(appointment.start)} - ${appointment.status}`}
                        secondary={`Serviciu: ${appointment.serviceId} | Sala: ${appointment.roomId}`}
                      />
                    </ListItemButton>
                  </ListItem>
                  {index < existingAppointments.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
            
            {selectedAppointment && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => handleStartFromAppointment(selectedAppointment)}
                  disabled={loading}
                >
                  Incepe Vizita din Programare
                </Button>
              </Box>
            )}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom>
          Vizita Ad-hoc (fara programare)
        </Typography>

        <Grid container spacing={2}>
          <Grid size={12}>
            <FormControl fullWidth required>
              <InputLabel>Medic</InputLabel>
              <Select
                value={formData.doctorId}
                onChange={(e) => setFormData(prev => ({ ...prev, doctorId: e.target.value }))}
                label="Medic"
              >
                {doctors.map((doctor) => (
                  <MenuItem key={doctor.id} value={doctor.id}>
                    {doctor.firstName} {doctor.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              required
              label="Motivul vizitei"
              value={formData.reason}
              onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
              placeholder="ex: consult de rutina, vaccinare, problema de sanatate..."
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Note"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Note suplimentare pentru vizita..."
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Anuleaza
        </Button>
        <Button 
          onClick={handleStartAdHocVisit} 
          variant="contained" 
          disabled={loading || !formData.doctorId || !formData.reason}
        >
          {loading ? <CircularProgress size={20} /> : 'Incepe Vizita Ad-hoc'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StartVisitDialog;
